import { Box, Container, Typography } from '@mui/material';
import { motion } from 'framer-motion';

const navy  = '#0F1D2E';
const navyL = '#1B2D42';
const gold  = '#D1BC8A';

/**
 * Compact, centered page hero for all non-home pages.
 * Height: ~200-260px (not a full-screen hero).
 */
export default function PageHero({ overline, title, description, backgroundImage }) {
  return (
    <Box sx={{
      position: 'relative', overflow: 'hidden',
      height: { xs: 200, md: 260 },
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center',
      bgcolor: navy,
    }}>
      {/* Background image */}
      {backgroundImage && (
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }} />
      )}
      {/* Background gradient */}
      <Box sx={{
        position: 'absolute', inset: 0,
        background: backgroundImage
          ? `linear-gradient(145deg, rgba(15,29,46,0.78) 0%, rgba(27,45,66,0.72) 50%, rgba(15,29,46,0.78) 100%)`
          : `
            radial-gradient(ellipse 70% 60% at 50% 55%, rgba(209,188,138,0.07) 0%, transparent 62%),
            linear-gradient(145deg, ${navy} 0%, ${navyL} 50%, ${navy} 100%)
          `,
      }} />

      {/* Blueprint grid lines */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
        `,
        backgroundSize: '36px 36px',
      }} />

      {/* Gold top accent line */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3, zIndex: 2,
        background: `linear-gradient(90deg, transparent 0%, ${gold} 30%, #E8DEC3 60%, ${gold} 100%)`,
        opacity: 0.9,
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          {overline && (
            <Typography sx={{
              color: gold, fontWeight: 700, display: 'block',
              fontSize: '0.7rem', textTransform: 'uppercase',
              letterSpacing: '0.22em', mb: 1.5,
            }}>
              {overline}
            </Typography>
          )}

          <Typography component="h1" sx={{
            color: '#fff', fontWeight: 900,
            fontSize: { xs: '1.8rem', sm: '2.4rem', md: '2.8rem' },
            lineHeight: 1.1, letterSpacing: '-0.025em',
          }}>
            {title}
          </Typography>

          {description && (
            <Typography sx={{
              color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem',
              lineHeight: 1.72, maxWidth: 500, mx: 'auto', mt: 1.5,
            }}>
              {description}
            </Typography>
          )}
        </motion.div>
      </Container>
    </Box>
  );
}
